import { Share2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import SchoolShareDialog from "@/components/shared/SchoolShareDialog";

/**
 * Dashboard CTA card that nudges school admins and teachers to share
 * the school's public donation page. Mirrors the parent-facing
 * SharePrimaryCard on Parents/StudentDetail: the share dialog is the
 * primary action, with a small "preview" link underneath that opens
 * the public page in a new tab so staff can see what donors see.
 *
 * `variant="primary"` (default) is the orange-tinted CTA used on the
 * school admin dashboard. `variant="neutral"` is a plain card used on
 * the teacher classroom dashboard so the share card sits alongside
 * the Log minutes CTA without competing with it visually.
 */
export default function ShareDonationPageCard({ url, schoolName, variant = "primary", tvDisplayUrl = null }) {
  if (!url) return null;

  const cardClass = variant === "neutral" ? "" : "border-primary/30 bg-primary/5";

  return (
    <Card className={cardClass}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Share2 className="h-5 w-5 text-primary" />
          Share your donation page
        </CardTitle>
        <CardDescription>
          Send this link in a newsletter, family email or community post. Donations go straight to the school.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="rounded-md border border-border bg-background px-3 py-2 font-mono text-xs sm:text-sm text-foreground truncate">
          {url}
        </div>
        <SchoolShareDialog
          url={url}
          schoolName={schoolName}
          triggerVariant={variant === "neutral" ? "outline" : "default"}
          triggerSize="default"
          triggerFullWidth
        />
        <p className="text-xs">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
          >
            See what donors will see →
          </a>
          {tvDisplayUrl && (
            <>
              <span className="mx-2 text-muted-foreground/60">·</span>
              <a
                href={tvDisplayUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
              >
                Open TV display →
              </a>
            </>
          )}
        </p>
      </CardContent>
    </Card>
  );
}
